import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2, ShieldAlert, WifiOff } from '@/lib/icons'
import type { TelegramLoginPayload } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { fetchAuthConfig, loginWithTelegram } from './api'

const WIDGET_SCRIPT_SRC = 'https://telegram.org/js/telegram-widget.js?22'

/**
 * Widget skripti (yoki uning ichidagi iframe) shu muddat ichida paydo bo'lmasa — xato deb
 * hisoblanadi (pastdagi effekt izohiga qarang).
 */
const WIDGET_LOAD_TIMEOUT_MS = 10_000

/**
 * Telegram Login Widget'ning global callback interfeysi.
 *
 * Widget oddiy `<script>` teg orqali ishlaydi (React komponenti emas) va autentifikatsiyadan
 * keyin `data-onauth="onTelegramAuth(user)"` atributida ko'rsatilgan `window.onTelegramAuth`
 * funksiyasini o'zi chaqiradi — bu Telegram tomonidan belgilangan qat'iy shartnoma,
 * o'zgartirib bo'lmaydi (https://core.telegram.org/widgets/login).
 */
declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramLoginPayload) => void
  }
}

interface LoginPageProps {
  /** Sessiya muvaffaqiyatli o'rnatilgach chaqiriladi (`App.tsx` sessiyani qayta so'raydi). */
  onSuccess: () => void
}

/**
 * Brauzer rejimidagi kirish sahifasi — panel Telegram tashqarisida (`isInsideTelegram()`
 * `false`) ochilganda ko'rsatiladi. Alohida login/parol yo'q (`docs/02-trd.md`): yagona
 * yo'l — Telegram Login Widget, natijasi `POST /api/auth/telegram` ga yuboriladi.
 *
 * ⚠️ Widget FAQAT BotFather'da `/setdomain` orqali sozlangan domenda ishlaydi (prod:
 * `bloger.ayub-nashr.uz`). `localhost`da Telegram skriptni yuklamaydi/tugmani chizmaydi —
 * shuning uchun dev muhitida (`import.meta.env.DEV`) tugma o'rniga eslatma ko'rsatiladi:
 * server `.env`dagi `DEV_AUTH_BYPASS_TELEGRAM_ID` initData tekshiruvini butunlay chetlab
 * o'tadi, login sahifasi umuman kerak bo'lmay qoladi (Telegram ichidan sinash kifoya).
 */
export function LoginPage({ onSuccess }: LoginPageProps) {
  const config = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: fetchAuthConfig,
  })

  const loginMutation = useMutation({
    mutationFn: loginWithTelegram,
    onSuccess,
  })

  // Widget skripti yuklanganda o'rnatilgan `window.onTelegramAuth` FAQAT bir marta
  // yaratiladi (pastdagi effekt `botUsername` o'zgarmasa qayta ishga tushmaydi), lekin
  // `loginMutation.mutate` har renderda yangi identifikatorga ega bo'lishi mumkin.
  // `ref` orqali eng so'nggi versiyasiga murojaat qilamiz — aks holda callback eskirgan
  // (stale) `mutate`ni chaqirib qolishi mumkin edi.
  const mutateRef = useRef(loginMutation.mutate)
  mutateRef.current = loginMutation.mutate

  const containerRef = useRef<HTMLDivElement>(null)
  const botUsername = config.data?.botUsername

  // Skript yuklanmadi (masalan reklama blokeri `telegram.org`ni butunlay bloklagan) yoki
  // yuklandi-yu, lekin widget o'zini (iframe) chizmadi (blokerlar bunga ham qodir — so'rovni
  // o'tkazadi, lekin natijani DOM'dan yashiradi). Ikkala holatda ham `retryToken` orqali
  // "Qayta urinish" bosilganda effekt qaytadan ishga tushiriladi (skript yangi <script> tegi
  // sifatida qayta qo'shiladi — brauzer uni qayta yuklashga urinadi).
  const [widgetLoadError, setWidgetLoadError] = useState(false)
  const [retryToken, setRetryToken] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!botUsername || !container) return

    window.onTelegramAuth = (user) => {
      mutateRef.current(user)
    }

    // Muvaffaqiyat yoki xato allaqachon qayd etilganini bildiradi — tozalash funksiyasi
    // ishga tushgandan keyin (masalan komponent unmount bo'lganda) `setTimeout` hali ham
    // otilib, endi mavjud bo'lmagan holatni yangilashga urinmasligi uchun.
    let settled = false

    const script = document.createElement('script')
    script.src = WIDGET_SCRIPT_SRC
    script.async = true
    script.setAttribute('data-telegram-login', botUsername)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-radius', '10')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')

    // Tarmoq xatosi yoki reklama bloker `telegram.org` so'rovining o'zini rad etsa —
    // avvalgi kodda bu HECH QANDAY signal bermas edi, foydalanuvchi tugmasiz bo'sh joy
    // ko'rardi (`onerror` mutlaqo tinglanmagan edi).
    script.onerror = () => {
      settled = true
      setWidgetLoadError(true)
    }

    // Ba'zi bloker/kengaytmalar so'rovning o'ziga ruxsat beradi (`onerror` chaqirilmaydi),
    // lekin widget iframe'ini DOM'ga qo'shishning oldini oladi — shu holatni ham ushlash
    // uchun oqilona muddatdan keyin iframe hali yo'qligini tekshiramiz.
    const timeoutId = window.setTimeout(() => {
      if (!settled && !container.querySelector('iframe')) {
        settled = true
        setWidgetLoadError(true)
      }
    }, WIDGET_LOAD_TIMEOUT_MS)

    container.appendChild(script)

    return () => {
      window.clearTimeout(timeoutId)
      delete window.onTelegramAuth
      container.replaceChildren()
    }
  }, [botUsername, retryToken])

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-brand-subtle text-brand">
        <ShieldAlert size={28} aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold">Ayub — Admin panel</h1>
      <p className="max-w-sm text-sm text-app-hint">Faqat adminlar kira oladi</p>

      <div className="mt-2 flex min-h-11 flex-col items-center gap-3">
        {config.isLoading && (
          <Loader2 size={24} className="animate-spin text-app-hint" aria-hidden="true" />
        )}

        {config.isError && <ConfigErrorState onRetry={() => void config.refetch()} />}

        {config.data &&
          (import.meta.env.DEV ? (
            <DevWidgetNotice />
          ) : widgetLoadError ? (
            <WidgetLoadErrorState
              onRetry={() => {
                setWidgetLoadError(false)
                setRetryToken((token) => token + 1)
              }}
            />
          ) : (
            // Telegram skripti shu konteyner ICHIGA `<script>` tegini qo'shadi va o'zi
            // widget tugmasini chizadi — React bolasi sifatida yozilmaydi, chunki bu
            // React'ning virtual DOM'iga bog'liq emas, tashqi skript DOM'ni to'g'ridan
            // -to'g'ri o'zgartiradi (yuqoridagi effekt izohi).
            <div ref={containerRef} />
          ))}

        {loginMutation.isPending && (
          <p className="flex items-center gap-2 text-sm text-app-hint">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            Tekshirilmoqda...
          </p>
        )}

        {loginMutation.isError && <LoginErrorState error={loginMutation.error} />}
      </div>
    </div>
  )
}

/** `GET /api/auth/config` muvaffaqiyatsiz bo'lganda — bu tarmoq/server xatosi, retry mumkin. */
function ConfigErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="flex items-center gap-2 text-sm text-stage-rejected-text">
        <WifiOff size={16} aria-hidden="true" />
        Server bilan aloqa yo'q
      </p>
      <Button size="sm" variant="secondary" onClick={onRetry}>
        Qayta urinish
      </Button>
    </div>
  )
}

/**
 * Login Widget skripti/iframe'i yuklanmagan holat (`onerror` yoki timeout, yuqoridagi effekt
 * izohiga qarang) — avval bu holatda tugmasiz bo'sh joy qolar edi, endi aniq xabar + qayta
 * urinish beriladi.
 */
function WidgetLoadErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="flex max-w-xs items-center gap-2 text-sm text-stage-rejected-text">
        <WifiOff size={16} className="shrink-0" aria-hidden="true" />
        Login tugmasini yuklab bo&apos;lmadi. Reklama blokeri yoki tarmoq muammosi bo&apos;lishi
        mumkin.
      </p>
      <Button size="sm" variant="secondary" onClick={onRetry}>
        Qayta urinish
      </Button>
    </div>
  )
}

/** `POST /api/auth/telegram` xatosi — 403 (admin emas) aniq xabar, qolgani qayta urinish. */
function LoginErrorState({ error }: { error: Error }) {
  const isNotAdmin = error instanceof ApiError && error.statusCode === 403

  if (isNotAdmin) {
    return (
      <p className="flex max-w-xs items-center gap-2 text-sm text-stage-rejected-text">
        <ShieldAlert size={16} className="shrink-0" aria-hidden="true" />
        Sizda ruxsat yo'q
      </p>
    )
  }

  return (
    <p className="flex items-center gap-2 text-sm text-stage-rejected-text">
      <WifiOff size={16} aria-hidden="true" />
      {error.message || "Kirishda xatolik yuz berdi. Qayta urinib ko'ring."}
    </p>
  )
}

/**
 * `localhost`da Telegram Login Widget ishlamaydi (domen BotFather'da sozlanmagan) —
 * bo'sh/singan tugma o'rniga adminni to'g'ri yo'lga yo'naltiruvchi eslatma.
 */
function DevWidgetNotice() {
  return (
    <div className="flex max-w-xs flex-col items-center gap-2 rounded-card border border-app-border bg-app-card p-3 text-xs text-app-hint">
      <AlertTriangle size={18} className="text-brand" aria-hidden="true" />
      <p>
        Login Widget faqat BotFather&apos;da sozlangan domenda (prod) ishlaydi — dev muhitida
        ko&apos;rinmaydi.
      </p>
      <p>
        Lokal test uchun server <code className="font-mono">.env</code> faylida{' '}
        <code className="font-mono">DEV_AUTH_BYPASS_TELEGRAM_ID</code> ni o&apos;rnating.
      </p>
    </div>
  )
}
